import * as Yup from 'yup'
import Swal from 'sweetalert2'
import { useFormik } from 'formik'
import { useParams } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import {
  abrirAbaCarregando,
  ButtonType,
  downloadArquivo,
  fecharAba,
  isIOS,
  ModalSize,
  showAlert,
  showAlertLoading,
  showError,
} from '../../../../_metronic/helpers'
import { downloadDocumentoAssinado, informacoesDocumentoAssinado } from './core/_request'
import { InformacaoAssinatura } from './core/_models'
import {
  AcaoLinktree,
  Button,
  FormInput,
  IconeBaixar,
  IconeDispensar,
  IconeDocumento,
  IconeValidar,
  IlustracaoCodigoDesbloqueio,
  LoadingIndicator,
  ORIENTACOES_PERFIL,
  RodapeConfiancaReceita,
  URL_VALIDAR_ICP,
  useModal,
} from '../../../components'
import { UsePrescricaoModal } from '../prescricao/components/modal/UsePrescricaoModal'

/**
 * Normaliza um rótulo livre para comparação tolerante a caixa, espaços e acentos.
 * Ex.: "  Descrição do Documento " -> "descricao do documento".
 */
const normalizaRotulo = (nome?: string): string =>
  String(nome ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

/**
 * Rótulos free-form (já normalizados) que NÃO devem aparecer em /assinatura.
 * Acoplado aos rótulos devolvidos pelo backend em /public/assinatura/informacoes:
 * se o backend renomear um rótulo, ele volta a aparecer aqui. A comparação é por
 * igualdade COMPLETA do rótulo normalizado (não substring), para não ocultar
 * acidentalmente pares legítimos como "Tipo do Documento".
 */
const CAMPOS_OCULTOS = [
  'nome do arquivo',
  'tamanho do arquivo',
  'descricao do documento',
  'tipo do documento',
  'atendimento',
]

/** Rótulo (normalizado) exibido com menor ênfase visual. */
const CAMPO_BAIXA_ENFASE = 'atendimento'

export const AssinaturaContent = () => {
  const { token } = useParams<{ token: string }>()
  const { showModal } = useModal()
  const [showForm, setShowForm] = useState(false)
  const [documentInfo, setDocumentInfo] = useState<InformacaoAssinatura[]>([])
  const [loadingInfo, setLoadingInfo] = useState(true)
  // Handle da aba pré-aberta no gesto de toque (iOS); redirecionada após o await.
  const abaIOSRef = useRef<Window | null>(null)
  // Marca que há uma aba aguardando o onSubmit; se a validação falhar (onSubmit
  // não é chamado), o efeito abaixo fecha a aba órfã.
  const aguardandoSubmitRef = useRef(false)

  useEffect(() => {
    const fetchDocumentInfo = async () => {
      try {
        if (!token) return
        const info = await informacoesDocumentoAssinado(token)
        setDocumentInfo(info)
      } catch (error) {
        setDocumentInfo([])
      } finally {
        setLoadingInfo(false)
      }
    }

    fetchDocumentInfo()
  }, [token])

  const validationSchema = Yup.object().shape({
    code: Yup.string().required('Código do paciente é obrigatório'),
  })

  const formik = useFormik({
    initialValues: {
      code: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      aguardandoSubmitRef.current = false
      const aba = abaIOSRef.current
      try {
        if (!token) {
          fecharAba(aba)
          abaIOSRef.current = null
          return
        }
        if (isIOS() && !aba) {
          showAlert({
            title: 'Erro!',
            text: 'Não foi possível abrir o documento. Verifique se os pop-ups estão liberados e tente novamente.',
            icon: 'error',
          })
          return
        }
        showAlertLoading()
        const arquivo = await downloadDocumentoAssinado(token, values.code)
        await downloadArquivo(arquivo, { abaIOS: aba })
        abaIOSRef.current = null
        Swal.close()
      } catch (error) {
        fecharAba(aba)
        abaIOSRef.current = null
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  // Fecha a aba órfã quando o usuário tenta enviar mas a validação falha (o
  // Formik não chama onSubmit nesse caso), evitando deixar aba em branco aberta.
  useEffect(() => {
    if (formik.submitCount > 0 && aguardandoSubmitRef.current && !formik.isSubmitting) {
      fecharAba(abaIOSRef.current)
      abaIOSRef.current = null
      aguardandoSubmitRef.current = false
    }
  }, [formik.submitCount, formik.isSubmitting])

  // Pré-abre a aba SÍNCRONA no clique do botão de envio (antes da validação
  // assíncrona do Formik), preservando a user activation no iOS.
  const handleSubmitClick = () => {
    if (!isIOS()) return
    fecharAba(abaIOSRef.current)
    abaIOSRef.current = abrirAbaCarregando()
    aguardandoSubmitRef.current = true
  }

  const handleDownloadClick = () => setShowForm(true)

  // Abre o fluxo de dispensação reaproveitando o modal do farmacêutico do /prescricao.
  // O localizador do /assinatura é repassado como token; o backend valida a baixa.
  const showDispensacao = () =>
    showModal({
      title: 'Informações do farmacêutico',
      content: <UsePrescricaoModal tokenPrescricao='' tokenPaciente='' />,
      size: ModalSize.Medium,
    })

  // Filtra os campos ocultos só no render; o state `documentInfo` permanece cru.
  const documentInfoVisivel = documentInfo.filter(
    (info) => !CAMPOS_OCULTOS.includes(normalizaRotulo(info.nome))
  )

  const handleBack = () => {
    fecharAba(abaIOSRef.current)
    abaIOSRef.current = null
    aguardandoSubmitRef.current = false
    setShowForm(false)
    formik.resetForm()
  }

  if (showForm) {
    return (
      <>
        <form className='w-100' onSubmit={formik.handleSubmit} noValidate>
          <p className='text-muted small mb-3'>
            O código de desbloqueio são os 6 dígitos logo abaixo do QR Code:
          </p>
          <IlustracaoCodigoDesbloqueio />
          <FormInput
            formik={formik}
            label='Código de desbloqueio'
            placeholder='Digite o código de desbloqueio'
            name='code'
            className='mb-8'
            infoText='6 dígitos abaixo do QR Code'
            isRequired
          />
          <Button
            type={ButtonType.Submit}
            className='btn-primary btn-auth-full'
            disabled={formik.isSubmitting}
            onClick={handleSubmitClick}
          >
            {formik.isSubmitting ? <LoadingIndicator /> : 'Baixar documento'}
          </Button>
          <Button className='btn-secondary btn-auth-full mt-3' onClick={handleBack}>
            Voltar
          </Button>
        </form>
        <RodapeConfiancaReceita />
      </>
    )
  }

  return (
    <div className='w-100'>
      <div className='text-start mb-10'>
        <h1 className='mb-3'>Escolha uma opção</h1>
        <p className='text-muted'>Você pode validar a assinatura ou baixar o documento assinado</p>
      </div>

      {loadingInfo ? (
        <div className='d-flex justify-content-center mb-5'>
          <LoadingIndicator />
        </div>
      ) : documentInfoVisivel.length > 0 ? (
        <div className='info-documento'>
          <div className='info-documento__header'>
            <span className='info-documento__icon'>
              <IconeDocumento />
            </span>
            <h2 className='info-documento__title'>Informações do documento</h2>
          </div>
          <dl className='info-documento__list'>
            {documentInfoVisivel.map((info, index) => {
              const baixaEnfase = normalizaRotulo(info.nome) === CAMPO_BAIXA_ENFASE
              return (
                <div
                  key={index}
                  className={`info-documento__row${
                    baixaEnfase ? ' info-documento__row--baixa' : ''
                  }`}
                >
                  <dt className='info-documento__label'>{info.nome || 'Informação'}</dt>
                  <dd className='info-documento__value'>{info.valor}</dd>
                </div>
              )
            })}
          </dl>
        </div>
      ) : null}

      <div className='acoes-linktree'>
        <AcaoLinktree
          icon={<IconeBaixar />}
          titulo='Baixar documento'
          microcopy={ORIENTACOES_PERFIL.paciente}
          onClick={handleDownloadClick}
        />
        <AcaoLinktree
          icon={<IconeValidar />}
          titulo='Validar assinatura'
          microcopy={ORIENTACOES_PERFIL.validador}
          href={URL_VALIDAR_ICP}
          target='_blank'
        />
        <AcaoLinktree
          icon={<IconeDispensar />}
          titulo='Registrar dispensação'
          microcopy={ORIENTACOES_PERFIL.farmacia}
          badge='Exclusivo para farmacêutico'
          onClick={showDispensacao}
        />
      </div>

      <RodapeConfiancaReceita />
    </div>
  )
}
